<template>
  <div class="game-container" ref="gameContainer">
    <QuestionRewardComponent
      v-if="showQuestionRewardComponent"
      :questionReward="collectionSound"
      :clickable="true"
      :customAudioUrl="'src/assets/gameSounds/starPop/star_pop_game_collection_prompt.mp3'"
      :customMessage="`Let's put the stars in the bag!`"
      :add-star-on-interaction="false"
      @reward-collected="emitGameFinished"
    />

    <Transition name="slide-down" appear>
      <div v-if="showCounter" class="game-counter">
        <img src="@/assets/images/counter_background.jpg" alt="" />
        <div class="points-counter">{{ elementsCollectedCounterDisplay }}</div>
      </div>
    </Transition>
    <div v-if="showIntro || showScore" class="info-container">
      <Transition name="pop-in" appear>
        <div v-if="showIntro" :key="introText" class="intro-container">
          <h1>{{ introText }}</h1>
        </div>
      </Transition>

      <Transition name="pop-in" appear>
        <div v-if="showScore && elementsCollected > 0" class="score-container">
          <h1>Great work!</h1>
          <p v-if="elementsCollected > 0">
            You collected <strong class="score">{{ elementsCollectedScoreDisplay }}</strong> star{{
              elementsCollected === 1 ? '' : 's'
            }}!
          </p>
        </div>
      </Transition>

      <Transition name="pop-in" appear>
        <div v-if="showScore && elementsCollected === 0" class="score-container">
          <h1>Those stars were fast!</h1>
          <p>Let’s catch some more next time!</p>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
// Types
import type { Ref } from 'vue'
import type { AudioPlayer, GameConfig, GameElement, ManagedTimeout } from '@/customTypes'

//Services
import FullScreen from '@/services/FullScreen.js'
import { useTimeout } from '@/utils/helpers'
// Components
import QuestionRewardComponent from '@/components/QuestionRewardComponent.vue'

// Stores
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useDataStore } from '@/stores/DataStore'
import { useMediaDevicesStore } from '@/stores/MediaDevicesStore'

// Static assets
import collectionSound from '@/assets/gameSounds/starPop/collection_sound.mp3'
import starPopGameCollectionPrompt from '@/assets/gameSounds/starPop/star_pop_game_collection_prompt.mp3'
import starPopGameIntro from '@/assets/gameSounds/starPop/star_pop_game_intro.mp3'
import starPopGameIntroReady from '@/assets/gameSounds/starPop/star_pop_game_intro-ready.mp3'
import starPopGameIntroOne from '@/assets/gameSounds/starPop/star_pop_game_intro-one.mp3'
import starPopGameIntroTwo from '@/assets/gameSounds/starPop/star_pop_game_intro-two.mp3'
import starPopGameIntroThree from '@/assets/gameSounds/starPop/star_pop_game_intro-three.mp3'
import starPopGameIntroGo from '@/assets/gameSounds/starPop/star_pop_game_intro-go.mp3'
import starPopGameOutro from '@/assets/gameSounds/starPop/star_pop_game_outro.mp3'
import starPopGameOutroNoScore from '@/assets/gameSounds/starPop/star_pop_game_outro_no_score.mp3'
import starImage from '@/assets/images/star.png'

const soundEffectsStore = useSoundEffectsStore()
const dataStore = useDataStore()
const mediaDevicesStore = useMediaDevicesStore()

// Emits
const emit = defineEmits(['game-finished'])

const config: GameConfig = {
  minSize: 0.08,
  maxSize: 0.14,
  minRotationSpeed: 1000,
  maxRotationSpeed: 5000,
  minVelocity: 2000,
  maxVelocity: 3000,
  minInterval: 500,
  maxInterval: 750,
  maxStars: 5,
  gameLength: 10,
  gameSounds: [
    { name: 'collection_sound', url: collectionSound },
    {
      name: 'star_pop_game_collection_prompt',
      url: starPopGameCollectionPrompt
    },
    { name: 'star_pop_game_intro', url: starPopGameIntro },
    {
      name: 'star_pop_game_intro-ready',
      url: starPopGameIntroReady
    },
    {
      name: 'star_pop_game_intro-one',
      url: starPopGameIntroOne
    },
    {
      name: 'star_pop_game_intro-two',
      url: starPopGameIntroTwo
    },
    {
      name: 'star_pop_game_intro-three',
      url: starPopGameIntroThree
    },
    {
      name: 'star_pop_game_intro-go',
      url: starPopGameIntroGo
    },
    { name: 'star_pop_game_outro', url: starPopGameOutro },
    {
      name: 'star_pop_game_outro_no_score',
      url: starPopGameOutroNoScore
    }
  ]
}

const { isIpad } = mediaDevicesStore
const { setManagedTimeout, clearManagedTimeout } = useTimeout()

const gameSoundEffects: Ref<AudioPlayer[]> = ref([])

const showIntro: Ref<boolean> = ref(false)
const introText: Ref<string> = ref('')
const showCounter: Ref<boolean> = ref(false)

const timeUp: Ref<boolean> = ref(false)
const timeUpTimeoutId = ref<ManagedTimeout | null>(null)
const showScore: Ref<boolean> = ref(false)

const elementsCollected: Ref<number> = ref(0)
const elementsCollectedCounterDisplay: Ref<number> = ref(0)
const elementsCollectedScoreDisplay: Ref<number> = ref(0)

const gameContainer: Ref<HTMLDivElement | null> = ref(null)
const elements: Ref<GameElement[]> = ref([])

const particleCount: number = 20
const starPopAnimationDuration: number = 250
const particlePopAnimationDuration: number = 500
const popInAnimationDuration: number = 500

const pointsCounted: Ref<boolean> = ref(false)
const outroFinished: Ref<boolean> = ref(false)

const showQuestionRewardComponent: Ref<boolean> = ref(false)

let nextId: number = 0

const lastCreatedElement: Ref<GameElement | null> = ref(null)

onMounted(async () => {
  await createAudioPlayerForTrack()

  if (isIpad) {
    FullScreen.cancel()
  }

  startGameIntro()
})

onUnmounted(() => {
  clearManagedTimeout(timeUpTimeoutId)
  timeUp.value = true
  elements.value.forEach((element) => {
    element.domElement?.remove()
  })
  elements.value = []
  gameSoundEffects.value.forEach((sound) => {
    sound.stop()
  })
  gameSoundEffects.value = []
  dataStore.setStarPopGameState(false)
})

function startGameIntro(): void {
  const introPlayer = gameSoundEffects.value.find((sound) => sound.id === 'star_pop_game_intro')
  if (!introPlayer) {
    return
  }
  showIntro.value = true

  const introSequence = [
    {
      time: 0,
      text: 'Get ready!',
      callback: () => {
        playSound('star_pop_game_intro-ready')
        showCounter.value = true
      }
    },
    { text: '3', callback: () => playSound('star_pop_game_intro-three') },
    { text: '2', callback: () => playSound('star_pop_game_intro-two') },
    { text: '1', callback: () => playSound('star_pop_game_intro-one') },
    { text: 'Go!', callback: () => playSound('star_pop_game_intro-go') },
    {
      callback: () => {
        showIntro.value = false
        startGame()
      }
    }
  ]
  introPlayer.on('end', () => {
    let index = 0

    function processNextStep() {
      if (index >= introSequence.length) return

      const { time = 1000, text, callback } = introSequence[index]
      setManagedTimeout(() => {
        if (text) introText.value = text
        if (callback) callback()
        index++
        processNextStep()
      }, time)
    }

    processNextStep()
  })
  playSound('star_pop_game_intro')
}

function startGame(): void {
  showCounter.value = true

  timeUpTimeoutId.value = setManagedTimeout(() => {
    timeUp.value = true
  }, config.gameLength * 1000)

  createElement()
}

const createElement = (): void => {
  const gameContainer: HTMLDivElement = document.querySelector('.game-container') as HTMLDivElement

  if (!gameContainer || timeUp.value || elements.value.length >= config.maxStars) return

  const newElement: GameElement = buildNewElementData(gameContainer)
  const { animationContainer, starContainer, star } = buildDomElementsForElement(newElement)

  starContainer.appendChild(star)
  animationContainer.appendChild(starContainer)
  gameContainer.appendChild(animationContainer)

  elements.value.push(newElement)

  // Force a reflow to restart a CSS animation after an element has been added to the DOM (important, otherwise element may be rendered in the wrong position)
  void animationContainer.offsetHeight

  animationContainer.addEventListener('transitionend', () => {
    removeElement(newElement.id)
  })

  animationContainer.addEventListener('pointerenter', () => {
    handleElementInteraction(newElement.id)
  })

  newElement.domElement = animationContainer

  lastCreatedElement.value = newElement
  const scheduleNextElement =
    Math.random() * (config.maxInterval - config.minInterval) + config.minInterval

  // Add move class to start the animation
  // animationContainer.classList.add('move')
  animationContainer.style.cssText += `
    transform: translateY(-${newElement.y + newElement.size}px);
    `

  setManagedTimeout(() => {
    createElement()
  }, scheduleNextElement)
}

/**
 * Build data for a new element
 * @param gameContainer The game container element
 * @returns The new element data
 */
const buildNewElementData = (gameContainer: HTMLDivElement): GameElement => {
  const screenWidth = window.innerWidth

  const containerRect = gameContainer.getBoundingClientRect()
  const size = (Math.random() * (config.maxSize - config.minSize) + config.minSize) * screenWidth
  const rotationSpeed =
    Math.random() * (config.maxRotationSpeed - config.minRotationSpeed) + config.minRotationSpeed
  const velocity = Math.random() * (config.maxVelocity - config.minVelocity) + config.minVelocity

  // Calculate x position to avoid overlap
  let x: number

  if (lastCreatedElement.value) {
    const lastElementRight = lastCreatedElement.value.x + lastCreatedElement.value.size
    const availableSpaceRight = containerRect.width - lastElementRight
    const availableSpaceLeft = lastCreatedElement.value.x

    // Enure no overlap with previous element
    if (Math.random() < 0.5 && availableSpaceRight >= size) {
      x = lastElementRight + Math.random() * (availableSpaceRight - size)
    } else if (availableSpaceLeft >= size) {
      x = Math.random() * (availableSpaceLeft - size)
    } else {
      x = Math.random() * (containerRect.width - size)
    }
  } else {
    // No previous element, place randomly
    x = Math.random() * (containerRect.width - size)
  }

  // Ensure x is within the container bounds
  x = Math.max(0, Math.min(x, containerRect.width - size))

  return {
    id: nextId++,
    x,
    y: containerRect.height,
    size,
    rotationSpeed,
    velocity,
    score: 1,
    collected: false
  }
}

/**
 * Build DOM elements for a new element from provided element data
 * @param element The element data
 * @returns The DOM elements
 */
const buildDomElementsForElement = (
  element: GameElement
): {
  animationContainer: HTMLDivElement
  starContainer: HTMLDivElement
  star: HTMLImageElement
} => {
  const animationContainer = document.createElement('div')
  animationContainer.id = element.id.toString()
  animationContainer.classList.add('animation-container')
  animationContainer.style.cssText = `
    position: absolute;
    top: ${element.y}px;
    left: ${element.x}px;
    width: ${element.size}px;
    height: ${element.size}px;
    --velocity: ${element.velocity}ms;
    --target: -${element.size}px;
    --rotation-speed: ${element.rotationSpeed}ms;
    `
  const starContainer = document.createElement('div')
  starContainer.classList.add('star-container')

  const star = document.createElement('img')
  star.src = starImage
  star.classList.add('star')

  return { animationContainer, starContainer, star }
}

/**
 * Create particles for a collected element
 * @param domElement The element DOM element
 */
function createParticles(domElement: HTMLDivElement): void {
  const particles = Array.from({ length: particleCount }, () => {
    const angle = Math.random() * 2 * Math.PI
    const distance = 50 + Math.random() * 50
    const translateX = Math.cos(angle) * distance
    const translateY = Math.sin(angle) * distance
    const scale = 0.5 + Math.random()

    return {
      style: {
        '--translate-x': `${translateX}px`,
        '--translate-y': `${translateY}px`,
        '--scale': scale.toString()
      }
    }
  })

  const particleContainer = document.createElement('div')
  particleContainer.classList.add('particle-container')
  domElement.appendChild(particleContainer)

  particles.forEach((particle) => {
    const particleElement = document.createElement('div')
    particleElement.classList.add('particle')
    particleElement.style.cssText = `
      --translate-x: ${particle.style['--translate-x']};
      --translate-y: ${particle.style['--translate-y']};
      --scale: ${particle.style['--scale']};
      `
    particleContainer.appendChild(particleElement)

    setManagedTimeout(() => {
      particleElement.classList.add('animate')
    }, 0)
  })
}

/**
 * create a score popup
 */
function createScorePopup(domElement: HTMLDivElement, score: number): void {
  const scorePopup = document.createElement('div')
  scorePopup.classList.add('score-popup')
  scorePopup.textContent = `+${score}`
  domElement.appendChild(scorePopup)

  nextTick(() => {
    scorePopup.classList.add('animate')
  })
}

/**
 * Handle interaction with an element
 * @param id The element ID
 */
const handleElementInteraction = (id: number): void => {
  const element = elements.value.find((el) => el.id === id)

  if (!element || element?.collected) return

  element.collected = true

  const domElement = element.domElement
  // Keep the dom element in the same position
  const distanceTraveled =
    gameContainer.value!.getBoundingClientRect().height - domElement!.getBoundingClientRect().top
  domElement!.style.cssText += `
    transform: translateY(-${distanceTraveled + 10}px);
    `
  void domElement!.offsetHeight

  element.domElement?.removeEventListener('pointerenter', (e) => {
    e.preventDefault()
    handleElementInteraction(element.id)
  })

  const starElement = domElement?.querySelector('.star')
  domElement!.classList.add('stopped')
  starElement?.classList.add('animate') // trigger the star pop animation
  createParticles(domElement!) // build and animate particles
  createScorePopup(domElement!, element.score || 1) // build and animate score popup

  playSound('collection_sound')

  setManagedTimeout(() => {
    onCollected(element.id, element.score || 1)
  }, starPopAnimationDuration + particlePopAnimationDuration)
}

const onCollected = (elementId: number, score: number): void => {
  elementsCollected.value += score
  elementsCollectedCounterDisplay.value += score
  removeElement(elementId)
}

const removeElement = (id: number): void => {
  const index = elements.value.findIndex((el) => el.id === id)
  const element = elements.value[index]

  if (!element) {
    return
  }

  element.domElement?.removeEventListener('pointerenter', () =>
    handleElementInteraction(element.id)
  )
  element.domElement?.removeEventListener('transitionend', () => removeElement(element.id))
  element.domElement?.remove()

  elements.value = elements.value.filter((el) => el.id !== id)

  createElement()
  checkIfGameFinished()
}

const checkIfGameFinished = (): void => {
  if (timeUp.value && elements.value.length === 0) {
    onGameFinished()
  }
}

const onGameFinished = (): void => {
  clearManagedTimeout(timeUpTimeoutId)
  const score = elementsCollected.value
  const scoreArray = Array.from({ length: score }, (_, i) => i)
  const interval = 75
  showScore.value = true

  dataStore.sendRewardUpdateToApi(score)
  playGameOutro()

  setManagedTimeout(() => {
    scoreArray.forEach((_, index) => {
      setManagedTimeout(
        () => {
          elementsCollectedCounterDisplay.value -= 1
          elementsCollectedScoreDisplay.value = index + 1

          if (index === scoreArray.length - 1) {
            pointsCounted.value = true
            if (outroFinished.value) {
              showCounter.value = false
              showQuestionRewardComponent.value = true
            }
          }
        },
        (index + 1) * interval
      )
    })
  }, popInAnimationDuration)
}

const playGameOutro = (): void => {
  const score = elementsCollected.value
  if (score > 0) {
    const outroPlayer = gameSoundEffects.value.find((sound) => sound.id === 'star_pop_game_outro')

    if (!outroPlayer) {
      return
    }
    outroPlayer.on('end', () => {
      outroFinished.value = true
      if (pointsCounted.value) {
        showCounter.value = false
        showQuestionRewardComponent.value = true
      }
    })
    playSound('star_pop_game_outro')
  } else {
    const outroPlayer = gameSoundEffects.value.find(
      (sound) => sound.id === 'star_pop_game_outro_no_score'
    )

    if (!outroPlayer) {
      return
    }
    outroPlayer.on('end', () => {
      outroFinished.value = true

      showCounter.value = false
      showQuestionRewardComponent.value = true
    })
    playSound('star_pop_game_outro_no_score')
  }
}

const emitGameFinished = (): void => {
  showQuestionRewardComponent.value = false
  dataStore.addStar(elementsCollected.value)

  setManagedTimeout(() => {
    emit('game-finished')
  }, 2000)
}

const createAudioPlayerForTrack = async (): Promise<void> => {
  const promises = config.gameSounds.map(async (sound) => {
    return soundEffectsStore.createPlayerForTrack({
      id: sound.name,
      name: sound.name,
      url: sound.url,
      volume: 1,
      loop: false
    })
  })

  gameSoundEffects.value = await Promise.all(promises)
}

const playSound = (soundName: string) => {
  soundEffectsStore.stopTrack(soundName)
  soundEffectsStore.playTrack(soundName)
}
</script>

<style lang="scss">
.game-container {
  width: 100vw;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.4);
  width: 100%;
  height: 100%;
  z-index: 1030;
}

.game-counter {
  position: absolute;
  top: 1rem;
  left: 50%;
  display: flex;
  border: 10px solid $primary;
  transform: translateX(-50%);
  border-radius: 999px;
  width: 14vh;
  height: 14vh;
  box-shadow: 0px 0px 20px 3px rgba(0, 0, 0, 0.3);

  img {
    width: 100%;
    height: 100%;
    border-radius: 999px;
  }

  .points-counter {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'Lilita One', sans-serif;
    font-size: 8vh;
    color: $white;
  }
}

.info-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .intro-container,
  .score-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    user-select: none;
    & > * {
      font-family: 'Lilita One', sans-serif;
    }
  }

  h1 {
    font-size: 7rem;
    color: $primary;
  }

  p {
    font-size: 3rem;
    color: $white;
  }

  .score {
    font-size: 4rem;
    color: $primary;
  }
}

.animation-container {
  position: absolute;
  cursor: pointer;
  touch-action: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-drag: none;
  outline: none;
  transition: transform var(--velocity) linear;

  &.stopped {
    transition: transform 999s linear !important;
  }
}

.star-container {
  width: 100%;
  height: 100%;
  z-index: 1;
  @include animation(spin var(--rotation-speed) linear infinite);
}

.star {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2;

  &.animate {
    @include animation(star-pop v-bind('`${starPopAnimationDuration}ms`') ease-out forwards);
  }
}

.particle {
  position: absolute;
  width: 15px;
  height: 15px;
  background-color: gold;
  border-radius: 50%;
  opacity: 0;
  top: 50%;
  left: 50%;
  border: 1px solid gold;

  &.animate {
    @include animation(
      pop-out v-bind('`${particlePopAnimationDuration}ms`')
        v-bind('`${starPopAnimationDuration}ms`') ease-out forwards
    );
  }
}

.score-popup {
  position: absolute;
  top: 50%;
  left: 50%;
  font-family: 'Lilita One', sans-serif;
  font-size: 2rem;
  color: $primary;
  font-weight: bold;
  z-index: 3;
  opacity: 0;

  &.animate {
    @include animation(
      score-pop v-bind('`${particlePopAnimationDuration}ms`')
        v-bind('`${starPopAnimationDuration}ms`') ease-out forwards
    );
  }
}

@include keyframes(score-pop) {
  0% {
    transform: translate3d(-50%, -50%, 0) scale(0.5);
    opacity: 1;
  }
  100% {
    transform: translate(200%, -200%) scale(1.25);
    opacity: 1;
  }
}

@include keyframes(pop-out) {
  0% {
    transform: translate(-50%, -50%) scale(0.1);
    opacity: 1;
  }
  100% {
    transform: translate(var(--translate-x), var(--translate-y)) scale(var(--scale));
    opacity: 0.3;
  }
}
@include keyframes(star-pop) {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(0);
  }
}

@include keyframes(spin) {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: top 1s;
}

.slide-down-enter-from,
.slide-down-leave-to {
  top: -100%;
}

.pop-in-enter-active {
  position: absolute;
  transition: all v-bind('`${popInAnimationDuration}ms`') ease-in;
}

.pop-in-enter-from {
  scale: 0.5;
  opacity: 0;
}
</style>
